import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Search, Sun, LayoutGrid, CircleDot, Stars, ArrowRight, BookOpen,
  Compass, Eclipse, Gauge, Sparkles, Eye, Timer, Orbit, Layers, Grid3X3, Infinity
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { fetchKBCategories, searchKB } from "@/lib/api";
import NavBar from "@/components/NavBar";

// Map icon strings to Lucide components
const CATEGORY_ICONS = {
  sun: Sun,
  "layout-grid": LayoutGrid,
  "circle-dot": CircleDot,
  stars: Stars,
  compass: Compass,
  eclipse: Eclipse,
  gauge: Gauge,
  sparkles: Sparkles,
  eye: Eye,
  timer: Timer,
  orbit: Orbit,
  layers: Layers,
  "grid-3x3": Grid3X3,
  infinity: Infinity,
};

// Color themes for each tier
const TIER_THEMES = {
  basics: {
    label: "The Building Blocks",
    description: "Master the core components of every Vedic chart",
    gradient: "from-amber-500/20 via-amber-500/5 to-transparent",
    accent: "amber",
    textColor: "text-amber-400",
    borderColor: "border-amber-500/20",
  },
  foundations: {
    label: "Foundations",
    description: "Understand why Vedic astrology works and how it differs from Western systems",
    gradient: "from-cyan-500/20 via-cyan-500/5 to-transparent",
    accent: "cyan",
    textColor: "text-cyan-400",
    borderColor: "border-cyan-500/20",
  },
  interpretation: {
    label: "Interpretation",
    description: "Learn to read planetary dignity, yogas, and aspects for chart analysis",
    gradient: "from-violet-500/20 via-violet-500/5 to-transparent",
    accent: "violet",
    textColor: "text-violet-400",
    borderColor: "border-violet-500/20",
  },
  prediction: {
    label: "Prediction",
    description: "Master timing techniques using Dashas and planetary transits",
    gradient: "from-rose-500/20 via-rose-500/5 to-transparent",
    accent: "rose",
    textColor: "text-rose-400",
    borderColor: "border-rose-500/20",
  },
  advanced: {
    label: "Advanced Topics",
    description: "Explore divisional charts, nakshatra padas, and karmic astrology",
    gradient: "from-emerald-500/20 via-emerald-500/5 to-transparent",
    accent: "emerald",
    textColor: "text-emerald-400",
    borderColor: "border-emerald-500/20",
  },
};

// Category-specific colors
const CATEGORY_COLORS = {
  // Basics
  planets: { bg: "rgba(251,191,36,0.08)", border: "rgba(251,191,36,0.25)", text: "text-amber-400" },
  houses: { bg: "rgba(99,102,241,0.08)", border: "rgba(99,102,241,0.25)", text: "text-indigo-400" },
  signs: { bg: "rgba(52,211,153,0.08)", border: "rgba(52,211,153,0.25)", text: "text-emerald-400" },
  nakshatras: { bg: "rgba(244,114,182,0.08)", border: "rgba(244,114,182,0.25)", text: "text-pink-400" },
  // Foundations
  foundations: { bg: "rgba(34,211,238,0.08)", border: "rgba(34,211,238,0.25)", text: "text-cyan-400" },
  "lunar-nodes": { bg: "rgba(139,92,246,0.08)", border: "rgba(139,92,246,0.25)", text: "text-violet-400" },
  // Interpretation
  "planetary-strength": { bg: "rgba(251,146,60,0.08)", border: "rgba(251,146,60,0.25)", text: "text-orange-400" },
  yogas: { bg: "rgba(168,85,247,0.08)", border: "rgba(168,85,247,0.25)", text: "text-purple-400" },
  aspects: { bg: "rgba(56,189,248,0.08)", border: "rgba(56,189,248,0.25)", text: "text-sky-400" },
  // Prediction
  dashas: { bg: "rgba(244,63,94,0.08)", border: "rgba(244,63,94,0.25)", text: "text-rose-400" },
  transits: { bg: "rgba(248,113,113,0.08)", border: "rgba(248,113,113,0.25)", text: "text-red-400" },
  // Advanced
  vargas: { bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.25)", text: "text-emerald-400" },
  "nakshatra-padas": { bg: "rgba(45,212,191,0.08)", border: "rgba(45,212,191,0.25)", text: "text-teal-400" },
  "karmic-astrology": { bg: "rgba(167,139,250,0.08)", border: "rgba(167,139,250,0.25)", text: "text-violet-400" },
};

// Group categories by tier
function groupByTier(categories) {
  const tierOrder = ["basics", "foundations", "interpretation", "prediction", "advanced"];
  const groups = {};
  
  categories.forEach((cat) => {
    const tier = cat.tier || "basics";
    if (!groups[tier]) groups[tier] = [];
    groups[tier].push(cat);
  });
  
  // Sort groups by tier order and sort categories within each group by order
  const sortedGroups = {};
  tierOrder.forEach((tier) => {
    if (groups[tier]) {
      sortedGroups[tier] = groups[tier].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
  });
  
  return sortedGroups;
}

function CategoryCard({ cat }) {
  const IconComp = CATEGORY_ICONS[cat.icon] || BookOpen;
  const colors = CATEGORY_COLORS[cat.slug] || { bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.1)", text: "text-white/70" };

  return (
    <Link
      to={`/learn/${cat.slug}`}
      className="group rounded-2xl p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
      style={{ background: colors.bg, border: `1px solid ${colors.border}` }}
      data-testid={`learn-category-${cat.slug}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${colors.text}`} style={{ background: "rgba(255,255,255,0.06)" }}>
          <IconComp className="w-4.5 h-4.5" />
        </div>
        <ArrowRight className="w-4 h-4 text-white/15 group-hover:text-white/50 transition-all duration-300 group-hover:translate-x-1" />
      </div>
      <h3 className="text-base font-medium text-white/90 mb-1 font-[Manrope]">{cat.title}</h3>
      <p className="text-xs text-white/35 mb-2 leading-relaxed font-[Manrope] line-clamp-2">{cat.description}</p>
      <span className="text-[10px] text-white/25 tracking-wider uppercase font-[Manrope]">
        {cat.article_count} {cat.article_count === 1 ? "article" : "articles"}
      </span>
    </Link>
  );
}

function TierSection({ tierKey, categories }) {
  const theme = TIER_THEMES[tierKey];
  if (!theme || !categories || categories.length === 0) return null;

  return (
    <section className="mb-12" data-testid={`tier-section-${tierKey}`}>
      {/* Tier Header */}
      <div className={`relative mb-6 pb-4 border-b ${theme.borderColor}`}>
        <div className={`absolute inset-0 bg-gradient-to-r ${theme.gradient} opacity-50 rounded-t-xl`} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs tracking-[0.2em] uppercase ${theme.textColor} font-medium font-[Manrope]`}>
              {theme.label}
            </span>
          </div>
          <p className="text-sm text-white/40 font-[Manrope]">{theme.description}</p>
        </div>
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {categories.map((cat) => (
          <CategoryCard key={cat.slug} cat={cat} />
        ))}
      </div>
    </section>
  );
}

export default function LearnLanding() {
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchKBCategories().then((d) => setCategories(d.categories)).catch(console.error);
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults(null); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchKB(searchQuery);
        setSearchResults(data.results);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const groupedCategories = groupByTier(categories);
  const totalArticles = categories.reduce((sum, c) => sum + (c.article_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#050505]">
      <NavBar />

      <main className="pt-14 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="pt-12 pb-10 text-center" data-testid="learn-hero">
          <div className="flex items-center justify-center gap-2 mb-3">
            <BookOpen className="w-5 h-5 text-white/30" />
            <span className="text-xs tracking-[0.3em] uppercase text-white/30 font-[Manrope]">Vedic Astrology Curriculum</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-light text-white/95 mb-3 font-[Manrope]">
            Learn Jyotish
          </h1>
          <p className="text-base text-white/40 max-w-2xl mx-auto mb-2 font-[Manrope]">
            A comprehensive curriculum from foundations to advanced techniques.
          </p>
          <p className="text-sm text-white/25 font-[Manrope]">
            {categories.length} topics &middot; {totalArticles} articles
          </p>

          {/* Search */}
          <div className="relative max-w-lg mx-auto mt-8" data-testid="learn-search">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              type="text"
              placeholder="Search topics, planets, yogas, dashas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 pr-4 h-12 bg-white/[0.04] border-white/10 rounded-full text-white/90 placeholder:text-white/25 focus:border-white/25 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm font-[Manrope]"
              data-testid="learn-search-input"
            />
          </div>

          {/* Search Results Dropdown */}
          {searchResults !== null && (
            <div className="max-w-lg mx-auto mt-2 rounded-xl overflow-hidden border border-white/10 bg-[#0A0A0A] text-left" data-testid="learn-search-results">
              {searching ? (
                <p className="p-4 text-white/30 text-sm">Searching...</p>
              ) : searchResults.length === 0 ? (
                <p className="p-4 text-white/30 text-sm">No results found for "{searchQuery}"</p>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  {searchResults.map((art) => (
                    <Link
                      key={art.slug}
                      to={`/learn/article/${art.slug}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                      data-testid={`search-result-${art.slug}`}
                    >
                      <div className="flex-1">
                        <p className="text-sm text-white/90 font-medium">{art.title}</p>
                        <p className="text-xs text-white/40">{art.subtitle}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider text-white/20 px-2 py-0.5 rounded-full border border-white/10 whitespace-nowrap">
                        {art.category_slug?.replace("-", " ")}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tiered Category Sections */}
        <div className="pb-20">
          {/* Our System: Tropical / Vedic */}
          <section className="mb-14" data-testid="learn-system-section">
            <div
              className="rounded-2xl p-6 sm:p-8"
              style={{ background: "rgba(34,211,238,0.05)", border: "1px solid rgba(34,211,238,0.18)" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Orbit className="w-5 h-5 text-cyan-400" />
                <span className="text-xs tracking-[0.2em] uppercase text-cyan-400 font-medium font-[Manrope]">
                  How We Calculate Your Chart
                </span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-light text-white/95 mb-4 font-[Manrope]">
                The Tropical / Vedic System
              </h2>

              <div className="space-y-4 text-sm sm:text-base text-white/60 leading-relaxed font-[Manrope] max-w-3xl">
                <p data-testid="learn-system-intro">
                  Daily Nakshatra combines tropical zodiac signs with sidereal nakshatras, interpreted through Vedic astrology.
                </p>

                <div className="flex items-start gap-3">
                  <Sun className="w-4 h-4 text-amber-400 mt-1 shrink-0" />
                  <p>
                    Your Sun, Moon, planetary signs, and rising sign use the <span className="text-white/90">tropical zodiac</span>, anchored to the equinoxes and solstices.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <Stars className="w-4 h-4 text-pink-400 mt-1 shrink-0" />
                  <p>
                    Your nakshatras—the 27 lunar mansions—use a <span className="text-white/90">star-based reference</span>, calculated with True Lahiri (True Chitra) ayanamsa. This correction accounts for the changing alignment between the tropical zodiac and the stars.
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <Timer className="w-4 h-4 text-rose-400 mt-1 shrink-0" />
                  <p>
                    We bring these calculations together using Vedic interpretive techniques. Nakshatra-based timing systems, including <span className="text-white/90">Vimshottari dasha</span>, continue to use your sidereal birth nakshatra.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 max-w-3xl">
                <h3 className="text-base font-medium text-white/90 mb-2 font-[Manrope]" data-testid="learn-system-faq-title">
                  Why might my chart look different elsewhere?
                </h3>
                <p className="text-sm text-white/50 leading-relaxed font-[Manrope]">
                  Many Vedic websites use sidereal calculations for both signs and nakshatras. Here, your zodiac signs are tropical, while your nakshatras remain sidereal. Different calculation settings can produce different placements without changing your birth information.
                </p>
              </div>
            </div>
          </section>

          <TierSection tierKey="basics" categories={groupedCategories.basics} />
          <TierSection tierKey="foundations" categories={groupedCategories.foundations} />
          <TierSection tierKey="interpretation" categories={groupedCategories.interpretation} />
          <TierSection tierKey="prediction" categories={groupedCategories.prediction} />
          <TierSection tierKey="advanced" categories={groupedCategories.advanced} />
        </div>
      </main>
    </div>
  );
}
